import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import classNames from 'classnames';
import CalendarPart from 'tea-component/es/calendar/CalendarPart';
import { CalendarWrapper } from 'tea-component/es/datepicker/CalendarWrapper';
import { CalendarTable } from '@/pages/home/components/RangePicker/CalendarTable';
import { RangePickerContext } from 'tea-component/es/datepicker/RangePickerContext';
import {
  isAfter,
  isBefore,
} from 'tea-component/es/datepicker/util';
import type {
  CalendarTableType,
  RangeDateType,
} from 'tea-component/es/calendar/DateProps';
import HourSlotList from './HourSlotList';
import type { IHourRangePanelProps, IHourSlot, THourRangeValue } from './types';
import {
  applyHourSlotToMoment,
  clampHourRange,
  getHourRangeCalendarRange,
  getInitialCalendarViews,
  isValidHourRangeValue,
} from './hourRange';

const cloneCalendarValue = (value: THourRangeValue): THourRangeValue => {
  const [start, end] = value;
  return [
    moment.isMoment(start) && start.isValid() ? start.clone() : start,
    moment.isMoment(end) && end.isValid() ? end.clone() : end,
  ];
};

// 点击面板外日期时，相邻双月不联动切换视图
const shouldCurViewChange = (
  from: string | undefined,
  nextDate: Moment,
  curStartView: Moment,
  curEndView: Moment,
  rangeType: 'start' | 'end',
): boolean => {
  if (from !== 'outside-date') {
    return true;
  }
  const diffMonths = (a: Moment, b: Moment) => {
    const years = b.year() - a.year();
    return b.month() - a.month() + years * 12;
  };
  if (diffMonths(curStartView, curEndView) === 1) {
    if (rangeType === 'start') {
      return diffMonths(curStartView, nextDate) !== 1;
    }
    return diffMonths(nextDate, curEndView) !== 1;
  }
  return true;
};

const HourRangePanel: React.FC<IHourRangePanelProps> = ({
  active,
  draftValue,
  panelStep,
  shortcuts,
  disabledDate,
  maxRangeDays,
  hourSlots,
  language,
  selectedShortcutName,
  translation,
  onDraftChange,
  onPanelStepChange,
  onShortcutSelect,
  onConfirm,
}) => {
  const isCN = ['cn', 'zh_CN'].includes(language || '');
  const [panelTypes, setPanelTypes] = useState<[CalendarTableType, CalendarTableType]>([
    'date',
    'date',
  ]);

  // 日历本地工作值，与 draftValue 解耦，避免点选触发视图重算
  const [curValue, setCurValue] = useState<THourRangeValue>(() =>
    cloneCalendarValue(draftValue),
  );

  const initialViews = getInitialCalendarViews(draftValue);
  const [curStartView, setStartCurView] = useState(initialViews.start);
  const [curEndView, setEndCurView] = useState(initialViews.end);
  const [hovered, setHovered] = useState<Moment | null>(null);

  const prevShortcutRef = useRef(selectedShortcutName);

  const restoreCalendarViews = useCallback((value: THourRangeValue) => {
    const views = getInitialCalendarViews(value);
    setStartCurView(views.start);
    setEndCurView(views.end);
  }, []);

  // 打开弹层 / 切回 date 步时从 draft 重置；不依赖 draftValue，避免点选循环
  useEffect(() => {
    if (!active || panelStep !== 'date') return;
    const next = cloneCalendarValue(draftValue);
    setCurValue(next);
    restoreCalendarViews(next);
  }, [active, panelStep]);

  useEffect(() => {
    if (!active || selectedShortcutName === prevShortcutRef.current) return;
    prevShortcutRef.current = selectedShortcutName;
    const next = cloneCalendarValue(draftValue);
    setCurValue(next);
    if (panelStep === 'date') {
      restoreCalendarViews(next);
    }
  }, [active, selectedShortcutName, draftValue, panelStep, restoreCalendarViews]);

  // 完整 range 且跨月时，双面板视图跟随选中值
  useEffect(() => {
    if (!active || panelStep !== 'date') return;
    if (!isValidHourRangeValue(curValue)) return;

    const [start, end] = curValue;
    const limit = 'month' as const;
    if (!start.isSame(end, limit)) {
      setStartCurView(start.clone());
    }
    if (!end.isSame(start, limit)) {
      setEndCurView(end.clone());
    }
  }, [curValue, active, panelStep]);

  const commitCalendarValue = useCallback(
    (nextValue: THourRangeValue) => {
      setCurValue(nextValue);
      onDraftChange(nextValue);
    },
    [onDraftChange],
  );

  const handleDateChange = useCallback(
    (nextValue: THourRangeValue) => {
      const normalized = nextValue.map((item, index) => {
        if (!moment.isMoment(item) || !item.isValid()) return item;
        const defaultSlot =
          index === 0 ? hourSlots[0] : hourSlots[hourSlots.length - 1];
        // 纯日期点选（00:00）补默认 hour slot
        if (item.hour() === 0 && item.minute() === 0) {
          return applyHourSlotToMoment(item, defaultSlot);
        }
        return item.clone().second(0).millisecond(0);
      }) as THourRangeValue;

      if (isValidHourRangeValue(normalized)) {
        const [clampedStart, clampedEnd] = clampHourRange(
          normalized[0],
          normalized[1],
          maxRangeDays,
          hourSlots,
        );
        commitCalendarValue([clampedStart, clampedEnd]);
        return;
      }

      commitCalendarValue(normalized);
    },
    [hourSlots, maxRangeDays, commitCalendarValue],
  );

  const handleSlotChange = useCallback(
    (index: 0 | 1, slot: IHourSlot) => {
      const base = curValue[index];
      if (!moment.isMoment(base) || !base.isValid()) return;
      const next = [...curValue] as THourRangeValue;
      next[index] = applyHourSlotToMoment(base, slot);
      if (isValidHourRangeValue(next)) {
        const [clampedStart, clampedEnd] = clampHourRange(
          next[0],
          next[1],
          maxRangeDays,
          hourSlots,
        );
        commitCalendarValue([clampedStart, clampedEnd]);
        return;
      }
      commitCalendarValue(next);
    },
    [curValue, hourSlots, maxRangeDays, commitCalendarValue],
  );

  const resolveCalendarDisabledDate = useCallback(
    (date: Moment): boolean => {
      if (!disabledDate) return true;
      const [start, end] = curValue;
      // 仅选 start 时把 anchor 传给 disabledDate
      if (moment.isMoment(start) && start.isValid() && !moment.isMoment(end)) {
        return disabledDate(date, start);
      }
      return disabledDate(date);
    },
    [disabledDate, curValue],
  );

  const handleStartViewChange = useCallback(
    (date: Moment, { from }: { from?: string; } = {}) => {
      if (!shouldCurViewChange(from, date, curStartView, curEndView, 'start')) {
        return;
      }
      if (!isAfter(curEndView, date, 'month')) {
        setEndCurView(moment(date).add(1, 'month'));
      }
      setStartCurView(date);
    },
    [curStartView, curEndView],
  );

  const handleEndViewChange = useCallback(
    (date: Moment, { from }: { from?: string; } = {}) => {
      if (!shouldCurViewChange(from, date, curStartView, curEndView, 'end')) {
        return;
      }
      if (!isBefore(curStartView, date, 'month')) {
        setStartCurView(moment(date).subtract(1, 'month'));
      }
      setEndCurView(date);
    },
    [curStartView, curEndView],
  );

  const shortcutHeader = useMemo(() => {
    if (!shortcuts?.length) return null;
    return shortcuts.map((item) => (
      <p
        key={item.name}
        className={classNames('dtb-rangepicker-shortcut', {
          'dtb-rangepicker-shortcut-hover': selectedShortcutName === item.name,
        })}
        onClick={() => onShortcutSelect(item.name)}
      >
        {isCN ? item.nameCN : item.name}
      </p>
    ));
  }, [shortcuts, selectedShortcutName, isCN, onShortcutSelect]);

  const showTimePanels = panelStep === 'time';

  return (
    <RangePickerContext.Provider value={{ range: undefined }}>
      <CalendarPart.Panel
        className={classNames('dtb-rangepicker-with-header', {
          'dbi-range-hour-picker__panel--time': showTimePanels,
        })}
        rangeMode
        timeMode={showTimePanels}
      >
        {shortcutHeader ? (
          <CalendarPart.Header>{shortcutHeader}</CalendarPart.Header>
        ) : null}
        {showTimePanels ? (
          <CalendarPart.Body>
            <div className="dbi-range-hour-picker__time-body">
              <div className="dbi-range-hour-picker__time-column">
                <div className="dbi-range-hour-picker__time-caption">
                  {moment.isMoment(curValue[0]) && curValue[0].isValid()
                    ? curValue[0].format('MM/DD/YYYY')
                    : translation.selectHour}
                </div>
                <HourSlotList
                  value={moment.isMoment(curValue[0]) ? curValue[0] : null}
                  slots={hourSlots}
                  onChange={(slot) => handleSlotChange(0, slot)}
                />
              </div>
              <div className="dbi-range-hour-picker__time-column">
                <div className="dbi-range-hour-picker__time-caption">
                  {moment.isMoment(curValue[1]) && curValue[1].isValid()
                    ? curValue[1].format('MM/DD/YYYY')
                    : translation.selectHour}
                </div>
                <HourSlotList
                  value={moment.isMoment(curValue[1]) ? curValue[1] : null}
                  slots={hourSlots}
                  onChange={(slot) => handleSlotChange(1, slot)}
                />
              </div>
            </div>
          </CalendarPart.Body>
        ) : (
          <CalendarWrapper
            type={panelTypes}
            value={curValue as unknown as RangeDateType}
            onChange={handleDateChange as unknown as (
              value: RangeDateType,
            ) => void}
            onTypeChange={(types) =>
              setPanelTypes(types as [CalendarTableType, CalendarTableType])
            }
          >
            {({ type, onTypeChange, range, ...calendarProps }) => {
              const [startRange, endRange] = getHourRangeCalendarRange(
                range,
                curStartView,
                curEndView,
              );
              return (
                <>
                  <CalendarTable
                    {...calendarProps}
                    calendarType="date"
                    rangeType="start"
                    range={startRange}
                    type={type[0]}
                    onTypeChange={(t) => onTypeChange([t, type[1]])}
                    showTime={false}
                    curViewMoment={curStartView}
                    onCurViewMomentChange={handleStartViewChange}
                    disabledDate={resolveCalendarDisabledDate}
                    dateRangeInRangePicker={range || [null, null]}
                    hovered={hovered}
                    onHoveredChange={setHovered}
                  />
                  <CalendarTable
                    {...calendarProps}
                    calendarType="date"
                    rangeType="end"
                    range={endRange}
                    type={type[1]}
                    onTypeChange={(t) => onTypeChange([type[0], t])}
                    showTime={false}
                    curViewMoment={curEndView}
                    onCurViewMomentChange={handleEndViewChange}
                    disabledDate={resolveCalendarDisabledDate}
                    dateRangeInRangePicker={range || [null, null]}
                    hovered={hovered}
                    onHoveredChange={setHovered}
                  />
                </>
              );
            }}
          </CalendarWrapper>
        )}
        <CalendarPart.Footer
          className="dbi-range-hour-picker__footer"
          left={
            <button
              type="button"
              className="dbi-range-hour-picker__footer-link"
              onClick={() =>
                onPanelStepChange(showTimePanels ? 'date' : 'time')
              }
            >
              {showTimePanels ? translation.selectDate : translation.selectHour}
            </button>
          }
          right={
            <button
              type="button"
              className="dbi-range-hour-picker__footer-confirm"
              onClick={onConfirm}
            >
              {translation.okText}
            </button>
          }
        />
      </CalendarPart.Panel>
    </RangePickerContext.Provider>
  );
};

export default HourRangePanel;
